import axios from 'axios';
import classNames from 'classnames';
import domtoimage from 'dom-to-image';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import moment from 'moment';
import React, {
  useState, useEffect, useRef,
} from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Row, Col, Badge, Spinner,
} from 'reactstrap';

import { upperPermanent, lowerPermanent } from '@config/message-teeth-data/permanent';
import { upperPrimary, lowerPrimary } from '@config/message-teeth-data/primary';
import { getReferralMessageAttachmentsRequest, downloadReferralMessageAttachmentRequest, generateSummaryFileRequest } from '@reducers/referral/ReferralAction';
import { handleError } from '@services/ErrorHandler';
import LocaleService from '@services/LocaleService';
import { CustomDocumentViewer } from '@shared/components/CustomDocumentViewer/CustomDocumentViewer';
import CustomReactMarkdown from '@shared/components/react-markdown/CustomReactMarkdown';
import {
  isClinicReferralSent, isDentistReferralSent, formatBytes, formatPhoneNumber,
  viewableAttachmentType,
  getFileExtension,
  getFileName,
  imageFileExtensions,
  formatDate, sanitizeFilename,
} from '@shared/helpers';
import { FileIcon, DownloadIcon, AttachmentTrashIcon } from '@shared/icons';

// import ReferralDetailsBlur from './ReferralDetailsBlur';
import DeleteAttachmentModal from '../delete-attachment-modal/DeleteAttachmentModal';
import ReferralDetailsSkeleton from '../skeleton-loading/ReferralDetailsSkeleton';

import '../../ReferralMessagesStyle.scss';

/**
 * ENH-053 — the no-login guest view (client, 2026-09-23 Discord: "show all
 * the referral details … exactly like what we show in the side bar … They just
 * can't respond to it"). The same panel, read-only: the referral is handed in
 * rather than read from redux, and files come through the guest link's own
 * endpoints instead of the signed-in ones.
 */
type GuestView = {
  referral: any;
  /** Resolves to a short-lived download URL for one file. */
  downloadAttachment: (attachment: any) => Promise<string>;
  /** Resolves to every file as { file_url, created_at }, for "Download all". */
  listAllAttachments: () => Promise<any[]>;
};

type ReferralDetailsProps = {
  loadingReferral: boolean;
  onViewImage: (_image: any) => void;
  guest?: GuestView;
};

export interface ReferralDetailsRef {
  generateTeethChartImage: () => Promise<string>;
}

const ReferralDetails = ({ loadingReferral, onViewImage, guest }: ReferralDetailsProps) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const navigate = useNavigate();
  const i18n = LocaleService.getTranslations('messages');
  const storeReferralData = useSelector(({ Referral }) => Referral.referralData);
  // Guests only ever view: no edit, no delete, no summary generation, no
  // paging (every file arrives at once).
  const readOnly = !!guest;
  const referralData = guest ? guest.referral : storeReferralData;
  const showReferrerContact = readOnly || !!referralData?.is_guest_submission;
  const teethChartRef = useRef<HTMLDivElement>(null);
  const userDetails = useSelector(({ Auth }) => Auth.user);
  const userId = userDetails?.id;
  // const affiliatedDentists = userDetails?.affiliated_dentists ?? [];
  const referralId = referralData?.id;
  const [ selectedTeeth, setSelectedTeeth ] = useState<Array<{
    tooth: string,
    type: string
  }>>([]);
  const [ specialtiesServices, setSpecialtiesServices ] = useState<any>([]);
  const [ downloading, setDownloading ] = useState('');
  const [ declineReason, setDeclineReason ] = useState<any>([]);
  const [ onHoverAttachment, setOnHoverAttachment ] = useState('');
  const [ toggleDeleteModal, setToggleDeleteModal ] = useState(false);
  const [ deleteMsgPayload, setDeleteMsgPayload ] = useState({});
  const [ loadedAttachments, setLoadedAttachments ] = useState<any>({});
  const [ loadingAttachments, setLoadingAttachments ] = useState(false);

  const generateTeethChartImage = async () => {
    if (!teethChartRef.current) {
      return '';
    }
    try {
      const dataUrl = await domtoimage.toPng(teethChartRef.current, {
        width: teethChartRef.current.clientWidth * 2,
        height: teethChartRef.current.clientHeight * 2,
        style: {
          transform: `scale(${2})`,
          transformOrigin: 'top left',
        },
      });
      return dataUrl;
    } catch (error) {
      handleError(error);
      return '';
    }
  };

  const handleDownloadSummary = async (type: 'print' | 'download') => {
    setDownloading(type === 'print' ? 'print' : 'summary');
    try {
      const payload: any = { id: referralId };
      if (!referralData.summary_file) {
        const chart = await generateTeethChartImage();
        if (!chart) {
          toast(i18n.summaryFile.noDataUrl);
          return;
        }
        payload.teeth_chart = chart;
      }
      payload.type = type;

      const result = await dispatch(generateSummaryFileRequest(payload)).$promise;

      // Phase 3 (backend): now answers {data: {file_url, ...}} instead of it directly.
      const downloadUrl = result.data?.data?.file_url;
      if (!downloadUrl) {
        toast(i18n.summaryFile.noDownloadUrl);
        return;
      }

      // Open in a new tab; protect against reverse tabnabbing and popup blockers
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      handleError(error);
    } finally {
      setDownloading('');
    }
  };

  const handleDownload = async (data: any, type: any) => {
    setDownloading(type);
    try {
      let downloadUrl: string;
      if (guest) {
        downloadUrl = await guest.downloadAttachment(data);
      } else {
        const newData = {
          id: referralId,
          attachment_id: data?.id,
          type: type === 'summary' ? 'summary' : 'attachment',
        };
        const result = await dispatch(downloadReferralMessageAttachmentRequest(newData)).$promise;
        downloadUrl = result.data?.data?.url;
      }
      const opened = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      if (opened) {
        opened.opener = null;
      } else {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a); // Safari
        a.click();
        a.remove();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setDownloading('');
    }
  };

  const handleDownloadAll = async () => {
    setDownloading('all');
    let attachments = [];
    try {
      if (guest) {
        attachments = await guest.listAllAttachments();
      } else {
        const result = await dispatch(getReferralMessageAttachmentsRequest(
          referralId, {
            for_download_all: true,
          },
        )).$promise;
        // Phase 3 (backend): now answers {data: [...]} instead of the array directly.
        attachments = result?.data?.data;
      }
    } catch (error) {
      setDownloading('');
      handleError(error);
    }
    if (attachments?.length === 0) {
      setDownloading('');
      return;
    }

    const zip = new JSZip();
    const getFileNameFromUrl = (url: any) => url.split('/').pop().split('?')[0];

    const fetchFile = async (url: any, date: any) => {
      try {
        const response = await axios.get(url, { responseType: 'blob' });
        // added date so that if there are similar filenames, it won't override
        const filename = `${date}-${getFileNameFromUrl(url)}`;
        const sanitizedFilename = sanitizeFilename(filename);
        zip.file(sanitizedFilename, response.data);
      } catch (error) {
        handleError(error);
      }
    };

    try {
      // Fetch all files
      await Promise.all(attachments
        .map((attachment: any) => fetchFile(attachment?.file_url, attachment?.created_at)));

      // Generate the zip file
      const content = await zip.generateAsync({ type: 'blob' });
      const date = moment(new Date()).format('YYYY-MM-DD-HH:mm:ss');

      // Save the zip file
      saveAs(content, `Referral Attachments - ${date}.zip`);
    } catch (error) {
      handleError(error);
    } finally {
      setDownloading('');
    }
  };

  const onDeleteMessageAttachment = (item: any) => {
    setDeleteMsgPayload({
      id: item?.id,
      referral_id: referralId,
    });
    setToggleDeleteModal(true);
  };

  const loadMoreAttachments = async () => {
    setLoadingAttachments(true);
    try {
      const currentPage = loadedAttachments?.meta?.current_page;
      const newData = {
        page: currentPage ? currentPage + 1 : 2,
        limit: 15,
        sort: 'created_at',
        order: 'desc',
      };
      const result = await dispatch(getReferralMessageAttachmentsRequest(
        referralId, newData,
      )).$promise;
      setLoadedAttachments(result?.data);
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleScrollY = ({ scrollTop, scrollHeight, clientHeight }: any) => {
    if (readOnly) return;
    if (!referralData?.attachments || loadingReferral || loadingAttachments) return;
    if (referralData?.attachments?.length < 15) return;
    if (loadedAttachments?.meta?.last_page && loadedAttachments?.meta?.current_page) {
      if (loadedAttachments?.meta?.last_page === loadedAttachments?.meta?.current_page) return;
    }
    if (scrollHeight - Math.floor(scrollTop) === clientHeight) {
      loadMoreAttachments();
    }
  };

  const isMasked = (item: string) => {
    if (!item) return false;
    return item.includes('****');
  };

  useEffect(() => {
    const initiate = () => {
      if (referralData?.status !== 'declined' || !referralData?.status_history) return;
      const reason = referralData?.status_history.message;
      if (!reason) return;
      const reasonArr = reason.split(', ');
      setDeclineReason(reasonArr);
    };

    initiate();

    return () => {
      setDeclineReason([]);
    };
  }, [ referralData?.status_history, referralData?.status ]);

  useEffect(() => {
    const initiate = () => {
      if (!referralData?.tooth || referralData?.tooth?.length === 0) return;
      setSelectedTeeth(referralData?.tooth);
    };

    initiate();

    // clear data when component unmounts
    return () => {
      setSelectedTeeth([]);
    };
  }, [ referralData?.tooth ]);

  useEffect(() => {
    const initiate = () => {
      if (!referralData?.services || referralData?.services?.length === 0) return;
      const formattedSpecialties: any = [];

      // Format the specialties and service for easier mapping
      referralData.services.forEach((specialty: any) => {
        const specialtyId = specialty?.id;
        const parentId = specialty?.parent_id;
        const specialtyName = specialty?.specialties;

        // If it's a specialty, add it to formattedSpecialties array
        if (specialtyName && !parentId) {
          const services: any = [];

          // Filter services that belong to this specialty
          referralData.services.forEach((service: any) => {
            if (service.parent_id === specialtyId) {
              const subServices: any = [];

              // Filter subServices that belong to this service
              referralData.services.forEach((subService: any) => {
                if (subService.parent_id === service.id) {
                  subServices.push({
                    name: subService.remarks ? `${subService.specialties}: ${subService.remarks}` : subService.specialties,
                  });
                }
              });

              services.push({
                name: service.remarks ? `${service.specialties}: ${service.remarks}` : service.specialties,
                subServices,
              });
            }
          });

          formattedSpecialties.push({
            name: specialtyName,
            services,
          });
        }
      });
      setSpecialtiesServices(formattedSpecialties);
    };

    initiate();

    // clear data when component unmounts
    return () => {
      setSpecialtiesServices([]);
    };
  }, [ referralData?.services ]);

  return (
    <>
      {/* The guest page carries its own heading, so the panel's is not repeated. */}
      {!readOnly && (
        <div className="referral-details-container">
          <div className="referral-details-title">
            <span style={{ flex: 1 }}>{i18n.referralDetails.title}</span>
            {([ 'completed', 'declined' ].indexOf(referralData?.status) === -1
              && ((isDentistReferralSent(userDetails, referralData))
                || (isClinicReferralSent(userDetails, referralData))))
              && (
                <span
                  className="edit-referral-btn-style"
                  onClick={() => navigate(`/view/${referralId}/referral`, {
                    state: {
                      // Keep existing state
                      ...location?.state,
                      // Add returnTo property
                      returnTo: location?.pathname,
                    },
                  })}
                >
                  {i18n.referralDetails.edit}
                </span>
              )}
          </div>
        </div>
      )}
      {loadingReferral && (
        <PerfectScrollbar
          style={{ height: '80vh', paddingBottom: '20px' }}
        >
          <ReferralDetailsSkeleton />
        </PerfectScrollbar>
      )}
      {!loadingReferral && (
        <PerfectScrollbar
          // The sidebar scrolls within the messages page; the guest page is
          // the details alone, so it grows with them and the page scrolls.
          style={{ height: readOnly ? 'auto' : '80vh', paddingBottom: readOnly ? 0 : '20px' }}
          onScrollY={handleScrollY}
        >
          {/* A summary is generated through a signed-in endpoint, and a guest's
              referral is never completed anyway: that happens only once the
              recipient has an account, which ends guest access (D7). */}
          {!readOnly && referralData?.status === 'completed' && (
            <div className="referral-details-container">
              <Row
                className={classNames('', {
                  'mb-3': referralData?.summary_filename,
                })}
                key={referralData?.summary_file_url}
              >
                <Col xs={6}>
                  {i18n.referralDetails.summary.title}
                </Col>
                <Col xs={6} className="text-end">
                  <a
                    onClick={() => (downloading === 'print' ? undefined
                      : handleDownloadSummary('print'))}
                    target="_blank"
                    className={classNames('download-attachments', {
                      'attachment-row-downloading': downloading === 'print',
                    })}
                    rel="noreferrer"
                  >
                    {downloading === 'print'
                      ? i18n.referralDetails.summary.buttons.printing
                      : i18n.referralDetails.summary.buttons.print}
                  </a>
                  {' | '}
                  <a
                    onClick={() => (downloading === 'summary' ? undefined
                      : handleDownloadSummary('download'))}
                    className={classNames('download-attachments', {
                      'attachment-row-downloading': downloading === 'summary',
                    })}
                  >
                    {downloading === 'summary'
                      ? i18n.referralDetails.summary.buttons.downloading
                      : i18n.referralDetails.summary.buttons.download}
                  </a>
                </Col>
              </Row>
              {referralData?.summary_filename && (
                <Row
                  className="attachment-row-style mb-2"
                >
                  <Col xs={2} className="file-icon-container">
                    <span className="file-icon"><FileIcon /></span>
                  </Col>
                  <Col xs={10}>
                    <div className="filename mb-2">
                      {referralData?.summary_filename}
                    </div>
                    <div className="file-size">
                      {formatBytes(referralData?.summary_file_size)}
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          )}
          <div className="referral-details-container">
            <Row className="mb-3">
              <Col xs={6}>
                <span className="referral-info-label">
                  {i18n.referralDetails.date}
                </span>
              </Col>
              <Col xs={6} className="text-end">
                <span className="referral-info-value">
                  {formatDate(referralData?.created_at)}
                </span>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={6}>
                <span className="referral-info-label">
                  {i18n.referralDetails.referenceNumber}
                </span>
              </Col>
              <Col xs={6} className="text-end">
                <span className="referral-info-value">
                  {referralData?.reference_no}
                </span>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={6}>
                <span className="referral-info-label">
                  {i18n.referralDetails.referredBy}
                </span>
              </Col>
              <Col xs={6} className="referred-group">
                <span className="referral-info-value">
                  {referralData?.referred_by?.name || referralData?.referred_by?.full_name}
                </span>
                <span className="referral-clinic-value">
                  {referralData?.referred_by_clinic?.name
                    ?? referralData?.referred_by_contact?.practice_name}
                </span>
              </Col>
            </Row>
            {/* A guest can't reply in Reforal, so the guest view shows how to
                reach the referrer (client, 2026-09-29 Discord). ENH-052: so does
                a clinic's view of a referral a guest sent through its link. */}
            {showReferrerContact && referralData?.referred_by_contact?.phone_number && (
              <Row className="mb-3">
                <Col xs={6}>
                  <span className="referral-info-label">
                    {i18n.referralDetails.referrerPhone}
                  </span>
                </Col>
                <Col xs={6} className="text-end">
                  <span className="referral-info-value">
                    {formatPhoneNumber(referralData.referred_by_contact.phone_number)}
                  </span>
                </Col>
              </Row>
            )}
            {showReferrerContact && referralData?.referred_by_contact?.email && (
              <Row className="mb-3">
                <Col xs={6}>
                  <span className="referral-info-label">
                    {i18n.referralDetails.referrerEmail}
                  </span>
                </Col>
                <Col xs={6} className="text-end">
                  <span className="referral-info-value">
                    {referralData.referred_by_contact.email}
                  </span>
                </Col>
              </Row>
            )}
            {/* The guest view names the patient under Patient Info (client, 2026-10-26). */}
            {!readOnly && (
              <Row className="mb-3">
                <Col xs={6}>
                  <span className="referral-info-label">
                    {i18n.referralDetails.patient}
                  </span>
                </Col>
                <Col xs={6} className="text-end">
                  <span
                    className={classNames('referral-info-value', {
                      'referral-info-value-blur': isMasked(referralData?.patient?.full_name),
                    })}
                  >
                    {referralData?.patient?.full_name}
                  </span>
                </Col>
              </Row>
            )}
            <Row className="mb-3">
              <Col xs={6}>
                <span className="referral-info-label">
                  {i18n.referralDetails.referredTo}
                </span>
              </Col>
              <Col xs={6} className="referred-group">
                {/* eslint-disable max-len */}
                <span className="referral-info-value">
                  {referralData?.referred_to?.full_name || referralData?.referred_to?.name || referralData?.referred_to?.email}
                </span>
                {/* eslint-enable max-len */}
                <span className="referral-clinic-value">
                  {referralData?.referred_to_clinic?.name}
                </span>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <span className="referral-info-label">
                  {i18n.referralDetails.sentBy}
                </span>
              </Col>
              <Col xs={6} className="text-end">
                <span className="referral-info-value">
                  {referralData?.sent_by || '-'}
                </span>
              </Col>
            </Row>
            {referralData?.status === 'completed' && referralData?.status_history?.message && (
              <Row className="mt-3">
                <Col xs={6}>
                  <span className="referral-info-completed-label">{i18n.referralDetails.completionNotes}</span>
                </Col>
                <Col xs={6} className="text-end">
                  <div className="referral-info-completed-value">
                    {referralData?.status_history?.message}
                  </div>
                </Col>
              </Row>
            )}
            {referralData?.status === 'completed' && referralData?.status_history?.completed_by && (
              <Row className="mt-3">
                <Col xs={6}>
                  <span className="referral-info-completed-label">
                    {i18n.referralDetails.completedBy}
                  </span>
                </Col>
                <Col xs={6} className="text-end">
                  <div className="referral-info-completed-value">
                    {referralData.status_history.completed_by}
                  </div>
                </Col>
              </Row>
            )}
            {referralData?.status === 'declined' && (
              <Row className="mt-3">
                <Col xs={6}>
                  <span className="referral-info-declined-label">{i18n.referralDetails.declineReason}</span>
                </Col>
                <Col xs={6} className="text-end">
                  {declineReason.length > 0 && declineReason.map((res: any, idx: number) => (
                    <div key={`${idx.toString()}`} className="referral-info-declined-value">
                      {res}
                    </div>
                  ))}
                </Col>
              </Row>
            )}
          </div>
          <div className="referral-details-container">
            <div className="referral-details-label mb-3">
              {i18n.referralDetails.patientInfo.title}
            </div>
            {readOnly && (
              <Row className="mb-3">
                <Col xs={4}>
                  <span className="referral-info-label">
                    {i18n.referralDetails.patientInfo.name}
                  </span>
                </Col>
                <Col xs={8} className="text-end">
                  <span
                    className={classNames('referral-info-value', {
                      'referral-info-value-blur': isMasked(referralData?.patient?.full_name),
                    })}
                  >
                    {referralData?.patient?.full_name}
                  </span>
                </Col>
              </Row>
            )}
            <Row className="mb-3">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientInfo.birthdate}
                </span>
              </Col>
              <Col xs={8} className="text-end">
                <span
                  className={classNames('referral-info-value', {
                    'referral-info-value-blur': isMasked(referralData?.patient?.birthdate),
                  })}
                >
                  {!isMasked(referralData?.patient?.birthdate)
                    ? formatDate(referralData?.patient?.birthdate)
                    : referralData?.patient?.birthdate}
                </span>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientInfo.email}
                </span>
              </Col>
              <Col xs={8} className="text-end">
                <span
                  className={classNames('referral-info-value', {
                    'referral-info-value-blur': isMasked(referralData?.patient?.email),
                  })}
                >
                  {referralData?.patient?.email || '-'}
                </span>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientInfo.mobile}
                </span>
              </Col>
              <Col xs={8} className="text-end">
                <span
                  className={classNames('referral-info-value', {
                    'referral-info-value-blur': isMasked(referralData?.patient?.mobile_number),
                  })}
                >
                  {!isMasked(referralData?.patient?.mobile_number)
                    ? formatPhoneNumber(referralData?.patient?.mobile_number)
                    : referralData?.patient?.mobile_number}
                </span>
              </Col>
            </Row>
            <Row className="">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientInfo.address}
                </span>
              </Col>
              <Col xs={8} className="text-end">
                <span
                  className={classNames('referral-info-value', {
                    'referral-info-value-blur': isMasked(referralData?.patient?.address?.full_address),
                  })}
                >
                  {referralData?.patient?.address?.full_address || '-'}
                </span>
              </Col>
            </Row>
          </div>
          <div className="referral-details-container">
            <div className="referral-details-label mb-3">
              {i18n.referralDetails.patientHistory.title}
            </div>
            <Row className="mb-3">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientHistory.medicalHistory}
                </span>
              </Col>
              <Col xs={8} style={{ display: 'flex', justifyContent: 'end' }}>
                <span className="medical-history-value">
                  {!isMasked(referralData?.patient?.medical_record?.medical_history)
                    && referralData?.patient?.medical_record?.medical_history && (
                    <CustomReactMarkdown
                      value={referralData?.patient?.medical_record?.medical_history}
                    />
                  )}
                  {!referralData?.patient?.medical_record?.medical_history && (
                    <span>
                      -
                    </span>
                  )}
                  {isMasked(referralData?.patient?.medical_record?.medical_history) && (
                    <span
                      className="referral-info-value-blur"
                    >
                      {referralData?.patient?.medical_record?.medical_history}
                    </span>
                  )}
                </span>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientHistory.allergies}
                </span>
              </Col>
              <Col xs={8} className="text-end">
                {!isMasked(referralData?.patient?.medical_record?.allergies)
                  && referralData?.patient?.medical_record?.allergies.length > 0 ? (
                    referralData?.patient?.medical_record?.allergies
                      .map((allergy: any, idx: number) => (
                        <div
                          key={`msg-allergy-${idx.toString()}`}
                          className="mb-1 referral-info-value"
                        >
                          {allergy}
                        </div>
                      ))
                  ) : (
                    <div>
                      {/* add "-" when empty */}
                      -
                    </div>
                  )}
                {isMasked(referralData?.patient?.medical_record?.allergies) && (
                  <span className="referral-info-value-blur">
                    {referralData?.patient?.medical_record?.allergies}
                  </span>
                )}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientHistory.specialConsiderations}
                </span>
              </Col>
              <Col xs={8} className="text-end">
                {!isMasked(referralData?.patient?.medical_record?.special_considerations)
                  && referralData?.patient?.medical_record?.special_considerations.length > 0 ? (
                    referralData?.patient?.medical_record?.special_considerations
                      .map((sc: any, idx: number) => (
                        <div
                          key={`msg-sc-${idx.toString()}`}
                          className="mb-1 referral-info-value"
                        >
                          {sc}
                        </div>
                      ))
                  ) : (
                    <div>
                      {/* add "-" when empty */}
                      -
                    </div>
                  )}
                {isMasked(referralData?.patient?.medical_record?.special_considerations) && (
                  <span className="referral-info-value-blur">
                    {referralData?.patient?.medical_record?.special_considerations}
                  </span>
                )}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={4}>
                <span className="referral-info-label">
                  {i18n.referralDetails.patientHistory.sedations}
                </span>
              </Col>
              <Col xs={8} className="text-end">
                {!isMasked(referralData?.patient?.medical_record?.need_sedations)
                  && referralData?.patient?.medical_record?.need_sedations.length > 0 ? (
                    referralData?.patient?.medical_record?.need_sedations
                      .map((sedation: any, idx: number) => (
                        <div
                          key={`msg-sedation-${idx.toString()}`}
                          className="mb-1 referral-info-value"
                        >
                          {sedation}
                        </div>
                      ))
                  ) : (
                    <div>
                      {/* add "-" when empty */}
                      -
                    </div>
                  )}
                {isMasked(referralData?.patient?.medical_record?.need_sedations) && (
                  <span className="referral-info-value-blur">
                    {referralData?.patient?.medical_record?.need_sedations}
                  </span>
                )}
              </Col>
            </Row>
          </div>
          <div className="referral-details-container">
            <div className="referral-details-label mb-3">
              {i18n.referralDetails.reasonForReferral}
            </div>
            <div id="teeth-chart" ref={teethChartRef}>
              <div className="mb-3">
                <Row className="permanent-upper-teeth">
                  {upperPermanent.map((u: any, index: number) => (
                    <Col
                      key={`${index.toString()}`}
                      className={classNames('permanent-teeth-style', {
                        'permanent-teeth-border': index + 1 === 8,
                        'selected-tooth-style': selectedTeeth.some((t) => t.tooth === u.value),
                      })}
                      style={{ paddingLeft: '2px', paddingRight: '2px' }}
                    >
                      <div>{u.icon}</div>
                      <div className="number-style">{u.label}</div>
                    </Col>
                  ))}
                </Row>
                <Row className="permanent-lower-teeth">
                  {lowerPermanent.map((u: any, index: number) => (
                    <Col
                      key={`${index.toString()}`}
                      className={classNames('permanent-teeth-style', {
                        'permanent-teeth-border': index + 1 === 8,
                        'selected-tooth-style': selectedTeeth.some((t) => t.tooth === u.value),
                      })}
                      style={{ paddingLeft: '2px', paddingRight: '2px' }}
                    >
                      <div className="number-style">{u.label}</div>
                      <div>{u.icon}</div>
                    </Col>
                  ))}
                </Row>
              </div>
              <div className="selected-tooth-container mb-3">
                {selectedTeeth.length > 0 && selectedTeeth
                  .map((t: any, index: number) => (
                    t.type === 'permanent' && (
                      <Badge
                        key={`${index.toString()}`}
                        pill
                        className="tooth-badge-style"
                      >
                        <div className="badge-value-container">
                          <span className="tooth-value">{t.tooth}</span>
                        </div>
                      </Badge>
                    )
                  ))}
              </div>
              <div className="mb-3">
                <Row className="primary-upper-teeth">
                  {upperPrimary.map((u: any, index: number) => (
                    <Col
                      key={`${index.toString()}`}
                      className={classNames('primary-teeth-style', {
                        'primary-teeth-border': index + 1 === 5,
                        'selected-tooth-style': selectedTeeth.some((t) => t.tooth === u.value),
                      })}
                      style={{ paddingLeft: '2px', paddingRight: '2px' }}
                    >
                      <div>{u.icon}</div>
                      <div className="number-style">{u.label}</div>
                    </Col>
                  ))}
                </Row>
                <Row className="primary-lower-teeth">
                  {lowerPrimary.map((u: any, index: number) => (
                    <Col
                      key={`${index.toString()}`}
                      className={classNames('primary-teeth-style', {
                        'primary-teeth-border': index + 1 === 5,
                        'selected-tooth-style': selectedTeeth.some((t) => t.tooth === u.value),
                      })}
                      style={{ paddingLeft: '2px', paddingRight: '2px' }}
                    >
                      <div className="number-style">{u.label}</div>
                      <div>{u.icon}</div>
                    </Col>
                  ))}
                </Row>
              </div>
              <div className="selected-tooth-container mb-3">
                {selectedTeeth.length > 0 && selectedTeeth
                  .map((t: any, index: number) => (
                    t.type === 'primary' && (
                      <Badge
                        key={`${index.toString()}`}
                        pill
                        className="tooth-badge-style"
                      >
                        <div className="badge-value-container">
                          <span className="tooth-value">{t.tooth}</span>
                        </div>
                      </Badge>
                    )
                  ))}
              </div>
            </div>
            {specialtiesServices && specialtiesServices.length > 0 && (
              <>
                <div className="referral-details-label mb-3">
                  {i18n.referralDetails.specialtiesServices}
                </div>
                <div className="specialties-services-container">
                  {specialtiesServices.length > 0 && specialtiesServices
                    .map((specialty: any, idx: number) => (
                      <Row
                        key={`${idx.toString()}`}
                        className="mb-3"
                      >
                        <Col xs={5}>
                          <span className="parent-service-label">{specialty?.name}</span>
                        </Col>
                        <Col xs={7} className="text-end">
                          {specialty?.services && specialty?.services.length > 0
                            && specialty.services.map((service: any, srvcIdx: number) => (
                              <span key={`${srvcIdx.toString()}`}>
                                <div
                                  className="service-label"
                                >
                                  <span>{service?.name}{service.subServices.length > 0 ? ': ' : ''}</span>
                                  <span>
                                    {service?.subServices && service?.subServices.length > 0
                                      && service.subServices
                                        .map((subService: any, subIdx: number) => (
                                          <span
                                            key={`${subIdx.toString()}`}
                                            className="service-label"
                                          >
                                            {/* Add comma and spacing until end of sub service */}
                                            {subService?.name}{service.subServices.length - 1 !== subIdx && ', '}
                                          </span>
                                        ))}
                                  </span>
                                  {srvcIdx !== specialty.services.length - 1 ? ', ' : ''}
                                </div>
                              </span>
                            ))}
                        </Col>
                      </Row>
                    ))}
                </div>
              </>
            )}
            {referralData?.remarks && (
              <>
                <div className="dotted-border mb-4 mt-4" />
                <div className="mb-2">
                  <Row>
                    <Col xs={3}>
                      <span className="remarks-label">{i18n.referralDetails.remarks}</span>
                    </Col>
                    <Col xs={9} style={{ display: 'flex', justifyContent: 'end' }}>
                      <span className="remarks-value">
                        {referralData?.remarks && (
                          <CustomReactMarkdown
                            value={referralData?.remarks}
                          />
                        )}
                        {!referralData?.remarks && (
                          <span>
                            -
                          </span>
                        )}
                      </span>
                    </Col>
                  </Row>
                </div>
              </>
            )}
          </div>
          {referralData?.attachments && referralData.attachments.length > 0 && (
            <div className="referral-details-container referral-details-container-no-border">
              <Row>
                <Col xs={6}>
                  <div className="referral-details-label mb-3">
                    {i18n.referralDetails.attachments.title}
                    <span className="referral-attachments-count">{referralData.attachments.length}</span>
                  </div>
                </Col>
                <Col xs={6} className="text-end">
                  <div
                    className={classNames('download-attachments mb-3', {
                      'downloading-attachments': downloading === 'all',
                    })}
                    onClick={downloading === 'all' ? undefined : handleDownloadAll}
                  >
                    {downloading === 'all'
                      ? i18n.referralDetails.attachments.downloading
                      : i18n.referralDetails.attachments.download}
                  </div>
                </Col>
              </Row>
              {referralData.attachments.map((attachment: any, idx: number) => (
                <Row
                  key={`${idx.toString()}`}
                  className={classNames('attachment-row-style', {
                    'mb-2 attachment-row-border': referralData.attachments.length - 1 !== idx,
                  })}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => { setOnHoverAttachment(attachment?.id); }}
                  onMouseLeave={() => setOnHoverAttachment('')}
                  onClick={() => {
                    const extensionName = getFileExtension(attachment.file) || '';
                    // for docx,pdf,excel
                    if (viewableAttachmentType.includes(extensionName)) {
                      CustomDocumentViewer.show({
                        url: attachment.file_url,
                        filename: attachment.filename
                          ? attachment.filename : getFileName(attachment.file),
                        showDownload: true,
                        customFileType: extensionName,
                        onDownload: async () => {
                          await handleDownload(
                            attachment,
                            attachment?.id,
                          );
                        },
                      });
                      return;
                    }
                    // for image
                    if (imageFileExtensions.includes(extensionName)) {
                      onViewImage(attachment);
                      return;
                    }
                    handleDownload(
                      attachment,
                      attachment?.id,
                    );
                  }}
                >
                  <Col xs={2} className="file-icon-container">
                    <span className="file-icon"><FileIcon /></span>
                  </Col>
                  <Col xs={8}>
                    <div className="filename mb-2">
                      {getFileName(attachment.file)}
                    </div>
                    <div className="file-size">
                      <span>{formatDate(attachment?.created_at)}</span>
                      <span style={{ marginRight: '5px', marginLeft: '5px' }}>•</span>
                      <span>{formatBytes(attachment?.file_size)}</span>
                    </div>
                  </Col>
                  <Col xs={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {onHoverAttachment === attachment?.id && (
                      <>
                        <div
                          style={{ cursor: 'pointer' }}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (downloading !== attachment?.id) {
                              handleDownload(
                                attachment,
                                attachment?.id,
                              );
                            }
                          }}
                        >
                          {downloading === attachment?.id
                            ? <Spinner className="download-attachment-spinner" size="sm" color="#D9D9D9" />
                            : <DownloadIcon />}
                        </div>
                        {!readOnly && referralData?.status !== 'completed' && attachment.sender_id === userId && (
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              onDeleteMessageAttachment(attachment);
                            }}
                          >
                            <AttachmentTrashIcon />
                          </div>
                        )}
                      </>
                    )}
                  </Col>
                </Row>
              ))}
              {loadingAttachments && (
                <span className="loading-attachments-spinner"><Spinner /></span>)}
            </div>
          )}
        </PerfectScrollbar>
      )}
      {!readOnly && (
        <DeleteAttachmentModal
          open={toggleDeleteModal}
          toggle={setToggleDeleteModal}
          payload={deleteMsgPayload}
        />
      )}
    </>
  );
};
export default ReferralDetails;
